import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

// ─── Worker initialization ───
// Production servers may serve .mjs files with wrong MIME type (application/octet-stream).
// Browsers enforce strict MIME checking for ES module scripts, which blocks the worker.
// Fix: Fetch the worker source as raw text (fetch ignores MIME type), then create a
// Blob URL with correct MIME type. This works regardless of server configuration.
const CDN_WORKER_URL = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

let workerInitPromise = null;
let workerReady = false;

function initWorker() {
  if (workerInitPromise) return workerInitPromise;

  workerInitPromise = (async () => {
    // Strategy 1: Fetch local worker file as raw text and create Blob URL
    // This bypasses MIME type checking entirely
    try {
      const response = await fetch('/pdf.worker.min.mjs');
      if (response.ok) {
        const workerText = await response.text();
        // Verify it's actual JavaScript content (not an HTML error page)
        if (workerText.length > 1000 && !workerText.startsWith('<!')) {
          const blob = new Blob([workerText], { type: 'application/javascript' });
          pdfjs.GlobalWorkerOptions.workerSrc = URL.createObjectURL(blob);
          workerReady = true;
          return;
        }
      }
    } catch (e) {
      // Local worker not available — continue to CDN fallback
    }

    // Strategy 2: Use CDN (requires CSP allowance for cdnjs.cloudflare.com)
    pdfjs.GlobalWorkerOptions.workerSrc = CDN_WORKER_URL;
    workerReady = true;
  })();

  return workerInitPromise;
}

/**
 * Reliable canvas-based PDF viewer for modals.
 * Uses pdf.js under the hood — works regardless of browser PDF plugin.
 *
 * Props:
 *  - pdfData: Uint8Array of PDF bytes (preferred — most reliable)
 *  - fallbackUrl: blob URL or direct URL for "Open in new tab" / "Download" fallbacks
 *  - file: (legacy) URL string — used if pdfData is not provided
 *  - className: optional wrapper class
 */
export const PdfPreviewViewer = ({ pdfData, fallbackUrl, file, className = '' }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [engineReady, setEngineReady] = useState(workerReady);

  // Initialize worker on first mount
  useEffect(() => {
    if (!engineReady) {
      initWorker().then(() => setEngineReady(true));
    }
  }, [engineReady]);

  // ── Stable document source ──
  // PDFVIEWER-FLICKER-001: previously `documentFile` was rebuilt as a NEW
  // object (`{ data: new Uint8Array(pdfData) }`) on every render, so each
  // page-nav re-render changed react-pdf's `file` prop identity and forced a
  // full document reload → whole-viewer flicker. Memoize on the raw source so
  // the Document loads exactly once for the modal's lifetime; page changes now
  // only swap the <Page>.
  const documentFile = useMemo(
    () => (pdfData ? { data: new Uint8Array(pdfData) } : file),
    [pdfData, file]
  );

  // URL for fallback open/download actions
  const actionUrl = fallbackUrl || (typeof file === 'string' ? file : null);

  const onDocumentLoadSuccess = useCallback(({ numPages: total }) => {
    setNumPages(total);
    setLoading(false);
    setError(null);
  }, []);

  const onDocumentLoadError = useCallback((err) => {
    console.error('PDF load error:', err);
    setError('Failed to load PDF document.');
    setLoading(false);
  }, []);

  // PDFVIEWER-FLICKER-001 — seamless page swap. `committedPage` holds the last
  // page that finished painting and stays visible; the incoming page renders
  // on top and, only once ITS canvas has painted, becomes the committed page.
  // During the swap the previous canvas sits behind the (briefly blank)
  // incoming one, so navigation never shows a blank frame.
  const [committedPage, setCommittedPage] = useState(1);
  useEffect(() => { setCommittedPage(1); }, [documentFile]);
  const onIncomingRendered = useCallback(() => setCommittedPage(pageNumber), [pageNumber]);

  // Don't render Document until the worker is initialized
  if (!engineReady) {
    return (
      <div className={`flex flex-col ${className}`} data-testid="pdf-preview-viewer">
        <div className="flex-1 overflow-auto bg-slate-100 flex justify-center border border-slate-200 rounded-lg">
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
            <p className="text-sm font-medium text-slate-600">Initializing PDF engine...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`} data-testid="pdf-preview-viewer">
      {/* Navigation bar */}
      {numPages && numPages > 0 && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-slate-100 border border-slate-200 rounded-t-lg text-xs text-slate-600">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPageNumber(p => Math.max(1, p - 1))}
              disabled={pageNumber <= 1}
              className="p-0.5 rounded hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="pdf-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span data-testid="pdf-page-info">
              Page {pageNumber} of {numPages}
            </span>
            <button
              type="button"
              onClick={() => setPageNumber(p => Math.min(numPages, p + 1))}
              disabled={pageNumber >= numPages}
              className="p-0.5 rounded hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="pdf-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            {actionUrl && (
              <>
                <a
                  href={actionUrl}
                  download="tax-cert-preview.pdf"
                  className="hover:text-blue-600 transition-colors"
                  data-testid="review-pdf-download"
                >
                  Download
                </a>
                <span className="text-slate-300">|</span>
              </>
            )}
            {actionUrl && (
              <button
                type="button"
                onClick={() => window.open(actionUrl, '_blank')}
                className="hover:text-blue-600 transition-colors"
                data-testid="review-pdf-new-tab"
              >
                Open in new tab
              </button>
            )}
          </div>
        </div>
      )}

      {/* PDF canvas area */}
      <div className={`flex-1 overflow-auto bg-slate-100 flex justify-center ${numPages ? 'border border-t-0 border-slate-200 rounded-b-lg' : 'border border-slate-200 rounded-lg'}`}>
        {loading && !error && (
          <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="review-pdf-loading">
            <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
            <p className="text-sm font-medium text-slate-600">Loading PDF...</p>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center py-16 gap-2 text-center">
            <p className="text-sm font-medium text-slate-600">{error}</p>
            {actionUrl && (
              <button
                type="button"
                onClick={() => window.open(actionUrl, '_blank')}
                className="text-sm text-blue-600 hover:text-blue-700 underline"
              >
                Open PDF in new tab instead
              </button>
            )}
          </div>
        )}

        {documentFile && (
          <Document
            file={documentFile}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading=""
            error=""
          >
            {numPages && (
              <div className="relative">
                {/* Previous page stays painted behind until the incoming one renders */}
                {committedPage !== pageNumber && (
                  <div className="absolute inset-0 flex justify-center" aria-hidden="true">
                    <Page
                      key={`hold-${committedPage}`}
                      pageNumber={committedPage}
                      width={720}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                      loading=""
                    />
                  </div>
                )}
                <Page
                  key={`live-${pageNumber}`}
                  pageNumber={pageNumber}
                  width={720}
                  renderAnnotationLayer={true}
                  renderTextLayer={true}
                  loading=""
                  onRenderSuccess={onIncomingRendered}
                />
              </div>
            )}
          </Document>
        )}
      </div>
    </div>
  );
};
